import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '../useBreakpoint';
import { buttonReset, color, font, FONT_FAMILY, radius, shadow, space, TAP, weight } from '../theme';
import Icon from './Icon';

/* A one-of-many choice behind a button: sorting ("מיון: עודכן לאחרונה ▾"), what a chart
   compares, or a filter with many values. Phones get a bottom sheet; desktop a popover
   under the button (px sizes only: the desktop content area is CSS-zoomed).
   - variant "text": a quiet blue text button, for the end of a ResultBar or a card header.
   - variant "chip": a chip in a filter row. It shows its label alone until a value other
     than `neutralValue` is chosen, then "label: value" in the selected chip style. */

export interface SelectOption<K extends string> {
  key: K;
  label: string;
  count?: number;
  group?: string; // options with the same group are listed under that heading, in order
}

interface SelectMenuProps<K extends string> {
  label: string;
  value: K;
  options: SelectOption<K>[];
  onChange: (key: K) => void;
  variant?: 'text' | 'chip';
  // Chip variant: the "no filter" value (e.g. 'הכל'), shown as the plain label
  neutralValue?: K;
  // Sheet / popover heading; defaults to the label
  title?: string;
  // Desktop popover edge: 'end' (left in RTL, for a button at the end of a line) or 'start'
  align?: 'start' | 'end';
}

const SHEET_Z = 400;

function SelectMenu<K extends string>({ label, value, options, onChange, variant = 'text', neutralValue, title, align = 'end' }: SelectMenuProps<K>) {
  const { isDesktop, isTablet } = useBreakpoint();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Opened from the keyboard (a click with detail 0): focus the chosen option; from a tap: the panel
  const byKeyboard = useRef(false);
  const current = options.find(o => o.key === value);
  const active = variant === 'chip' && neutralValue !== undefined && value !== neutralValue;

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  };
  const choose = (key: K) => { onChange(key); close(true); };
  // The effect below reads close through a ref, so it runs only when the menu opens or closes
  const closeRef = useRef(close);
  closeRef.current = close;

  // Esc closes; a click outside closes the popover; the phone sheet locks page scrolling
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); closeRef.current(true); } };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(t) && buttonRef.current && !buttonRef.current.contains(t)) closeRef.current(false);
    };
    document.addEventListener('keydown', onKey);
    if (isDesktop) document.addEventListener('mousedown', onDown);
    const prevOverflow = document.body.style.overflow;
    if (!isDesktop) document.body.style.overflow = 'hidden';
    // Keyboard: start on the chosen option. Pointer: focus the panel, so no focus ring shows
    const panel = panelRef.current;
    const checked = panel && panel.querySelector<HTMLElement>('[aria-checked="true"]');
    if (byKeyboard.current && checked) checked.focus();
    else if (panel) panel.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isDesktop]);

  // Arrow keys move between options, as in a native radio group
  const onListKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
    const i = items.indexOf(document.activeElement as HTMLElement);
    const checkedIndex = items.findIndex(el => el.getAttribute('aria-checked') === 'true');
    const next = i < 0
      ? items[Math.max(0, checkedIndex)]
      : items[Math.max(0, Math.min(items.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) next.focus();
  };

  /* ── The button ── */
  const valueText = current ? current.label : '';
  const trigger = variant === 'text'
    ? (
      <button ref={buttonRef} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={e => { byKeyboard.current = e.detail === 0; setOpen(!open); }}
        style={{ ...buttonReset, display: 'inline-flex', alignItems: 'center', gap: space.xs, minHeight: TAP, padding: `0 ${space.xs}px`, fontSize: font.small, color: color.primary, whiteSpace: 'nowrap' }}>
        <span style={{ color: color.textSecondary }}>{label}:</span>
        <span style={{ fontWeight: weight.bold }}>{valueText}</span>
        <Icon name="chevronDown" size={16} strokeWidth={2.2} />
      </button>
    )
    : (
      <button ref={buttonRef} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={e => { byKeyboard.current = e.detail === 0; setOpen(!open); }}
        style={{ ...buttonReset, flex: '0 0 auto', padding: '4px 0' }}>
        <span style={{
          display: 'flex', alignItems: 'center', gap: space.xs + 2, height: 36, padding: `0 ${space.sm + 2}px 0 ${space.md + 2}px`,
          borderRadius: radius.pill, fontSize: font.label, whiteSpace: 'nowrap',
          fontWeight: active ? weight.bold : weight.medium,
          color: active ? color.primary : color.text,
          background: active ? color.primarySoft : color.surface,
          boxShadow: active ? 'inset 0 0 0 1.5px rgba(24,95,165,0.45)' : shadow.raised,
        }}>
          {active ? `${label}: ${valueText}` : label}
          <Icon name="chevronDown" size={16} strokeWidth={2.2} />
        </span>
      </button>
    );

  /* ── The options ── */
  const groups: { name: string | undefined; items: SelectOption<K>[] }[] = [];
  options.forEach(o => {
    const last = groups[groups.length - 1];
    if (last && last.name === o.group) last.items.push(o);
    else groups.push({ name: o.group, items: [o] });
  });
  const list = (
    <div role="radiogroup" aria-labelledby={titleId}>
      {groups.map((g, gi) => (
        <div key={g.name || gi}>
          {g.name && <div style={{ padding: `${space.sm}px ${space.lg}px ${space.xxs}px`, fontSize: font.caption, fontWeight: weight.bold, color: color.textSecondary }}>{g.name}</div>}
          {g.items.map(o => {
            const checked = o.key === value;
            return (
              <button key={o.key} type="button" role="radio" aria-checked={checked} onClick={() => choose(o.key)}
                style={{
                  ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: isDesktop ? 40 : 48,
                  padding: `0 ${space.lg}px`, display: 'flex', alignItems: 'center', gap: space.md,
                  fontSize: font.label, fontWeight: checked ? weight.bold : weight.regular,
                  color: checked ? color.primary : color.text, background: checked ? color.primarySoft : 'transparent',
                }}>
                <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
                {o.count !== undefined && <span style={{ fontSize: font.small, color: checked ? color.primary : color.textSecondary }}>{o.count.toLocaleString('he-IL')}</span>}
                <span style={{ width: 18, display: 'flex' }}>{checked && <Icon name="check" size={18} strokeWidth={2.4} />}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );

  const heading = title || label;

  // Desktop: a popover under the button
  if (isDesktop) {
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        {trigger}
        {open && (
          <div ref={panelRef} role="dialog" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onListKey}
            style={{
              outline: 'none',
              position: 'absolute', top: 'calc(100% + 4px)', [align === 'end' ? 'left' : 'right']: 0, zIndex: 300,
              minWidth: 240, maxHeight: 420, overflowY: 'auto', boxSizing: 'border-box',
              background: color.surface, borderRadius: radius.lg, boxShadow: shadow.floating, padding: `${space.xs}px 0`, textAlign: 'right',
            }}>
            <div id={titleId} style={{ padding: `${space.sm}px ${space.lg}px ${space.xs}px`, fontSize: font.small, fontWeight: weight.bold, color: color.text }}>{heading}</div>
            {list}
          </div>
        )}
      </span>
    );
  }

  // Phone and tablet: a bottom sheet over a dimmed page (portaled, so no ancestor clips it)
  return (
    <>
      {trigger}
      {open && createPortal(
        <div dir="rtl" style={{ fontFamily: FONT_FAMILY, color: color.text }}>
          <div onClick={() => close(true)} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: SHEET_Z, background: 'rgba(16,24,40,0.38)' }} />
          <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onListKey}
            style={{
              outline: 'none',
              position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', zIndex: SHEET_Z + 1,
              width: '100%', maxWidth: isTablet ? 720 : 480, maxHeight: '75vh', overflowY: 'auto', boxSizing: 'border-box',
              background: color.surface, borderRadius: `${radius.xl + 4}px ${radius.xl + 4}px 0 0`, boxShadow: shadow.floating,
              padding: `${space.sm}px 0 calc(${space.lg}px + env(safe-area-inset-bottom))`,
            }}>
            <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2, background: color.track, margin: `0 auto ${space.sm}px` }} />
            <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${space.lg}px ${space.xs}px` }}>
              <h2 id={titleId} style={{ margin: 0, flex: 1, fontSize: font.heading, fontWeight: weight.bold }}>{heading}</h2>
              <button type="button" aria-label="סגירה" onClick={() => close(true)}
                style={{ ...buttonReset, width: TAP, height: TAP, marginInlineEnd: -space.sm, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.textSecondary }}>
                <Icon name="close" size={20} />
              </button>
            </div>
            {list}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/* The line above a list: the result count at the start, and the list's sort menu (or
   another action) at the end. Every list page uses it, so they all read the same way. */
export const ResultBar = ({ children, action, style }: { children: React.ReactNode; action?: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.sm, minHeight: TAP, ...style }}>
    <span role="status" style={{ fontSize: font.small, color: color.textSecondary, minWidth: 0 }}>{children}</span>
    {action}
  </div>
);

export default SelectMenu;
